import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {toast} from 'sonner'
import DashboardService from '../service/dashboard.services'
import {TNetworkDashboard, TDashboardFilters} from '../types/dashboard.type'
import {ErrorHelper, EShift} from '../types/app.type'
import {clampDateList, getCurrentMonthDates} from '../utils/dashboard.functions'
import {getTranslation} from '../utils/getTranslation'
import {
  useDashboardPeriod,
  useDashboardShift,
  useDashboardActions,
} from '../store'
import {useFilterTracking} from './filterTracking.hooks'

// Backend caps the dashboard window at 31 days; clamp client-side to match.
const MAX_DASHBOARD_PERIOD_DAYS = 31

/**
 * Drives the dashboard's period + shift controls, hoisted into the global header
 * (like `useListPeriodPicker` for the lists). Both bind to the dashboard's own
 * filter store — kept separate from the shared project-view filters on purpose —
 * so the header controls and the screen's query read one source of truth. The
 * period defaults to the current month, resolved at render so it never goes
 * stale in storage; a picked span longer than the cap is clamped (+ a toast).
 */
export const useDashboardHeaderFilters = () => {
  const dashboardPeriod = useDashboardPeriod()
  const selectedShift = useDashboardShift()
  const {setDashboardPeriod, setDashboardShift} = useDashboardActions()
  const trackFilter = useFilterTracking()

  const periodValue = useMemo(
    () => dashboardPeriod ?? getCurrentMonthDates(new Date()),
    [dashboardPeriod],
  )

  const onChangePeriod = (dates: string[]) => {
    // Ignore an incomplete/cleared selection: keep the last valid period.
    if (dates.length === 0) return
    if (dates.length > MAX_DASHBOARD_PERIOD_DAYS) {
      toast.warning(getTranslation('dashboard:periodMaxHelper'))
    }
    setDashboardPeriod(clampDateList(dates, MAX_DASHBOARD_PERIOD_DAYS))
  }

  const onChangeShift = (shift: EShift) => {
    setDashboardShift(shift)
    trackFilter('dashboardShift')
  }

  return {periodValue, onChangePeriod, selectedShift, onChangeShift}
}

export const useGetNetworkDashboard = (
  networkId: string | undefined,
  from: string,
  to: string,
  filters: TDashboardFilters,
) => {
  // Sorted copies so the key is stable regardless of user click order.
  const teamsKey = [...filters.teamIds].sort().join(',')
  const typesKey = [...filters.activityTypes].sort().join(',')
  const statusesKey = [...filters.statuses].sort().join(',')
  const shiftKey = filters.shift

  const {data, isLoading, isError, error} = useQuery<
    TNetworkDashboard,
    ErrorHelper
  >({
    queryKey: [
      'networkDashboard',
      networkId,
      from,
      to,
      teamsKey,
      typesKey,
      statusesKey,
      shiftKey,
    ],
    queryFn: () =>
      DashboardService.getNetworkDashboard(
        networkId as string,
        from,
        to,
        filters,
      ),
    enabled: !!networkId && !!from && !!to,
    // Volatile: depends on activity create/approve mutations elsewhere.
    staleTime: 0,
  })
  return {data, isLoading, isError, error}
}
